import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output } from '@angular/core';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { TwoFactorAuthComponent } from 'app/components/app-layout/settings/user/security/two-factor-auth/two-factor-auth.component';
import { GoogleAuthSetupStepComponent } from 'app/components/v2-layout/shared/two-factor-setup-steps/google-auth-setup-step.component';
import { SmsAuthSetupStepComponent } from 'app/components/v2-layout/shared/two-factor-setup-steps/sms-auth-setup-step.component';
import { RECOVERY_CODES_GATE_COPY } from 'app/components/v2-layout/shared/two-factor-setup-steps/recovery-codes-copy';
import { TwoFactorGateReason } from 'app/enums/two-factor-gate-reason.enum';

/** Friendly, reason-specific copy shown at the top of the gate dialog. */
export interface TwoFactorGateCopy
{
    title: string;
    message: string;
}

/**
 * Reason -> copy map. No user-facing strings live in the template; every gate
 * entry point supplies a {@link TwoFactorGateReason} and the matching copy is
 * resolved here.
 */
export const TWO_FACTOR_GATE_COPY: Record<TwoFactorGateReason, TwoFactorGateCopy> = {
    [TwoFactorGateReason.TREASURY_LOGIN]: {
        title: 'Set up two-factor authentication',
        message: 'Your organization uses Financial Accounts, so two-factor authentication is required to protect them. Set up an authenticator app or SMS verification below to continue to your dashboard.',
    },
    [TwoFactorGateReason.TREASURY_LOGIN_CARDHOLDER]: {
        title: 'Set up two-factor authentication',
        message: 'You have a PayHOA spend card, so two-factor authentication is required to protect your card and its transactions. Set up an authenticator app or SMS verification below to continue to your dashboard.',
    },
    [TwoFactorGateReason.TREASURY_KYC]: {
        title: 'Secure your account before Financial Accounts verification',
        message: 'Two-factor authentication is required before you can verify your organization\'s Financial Account. Set up an authenticator app or SMS verification below to continue.',
    },
};

export { RECOVERY_CODES_GATE_COPY };

/** Fallback copy for an absent/unknown reason (defensive — reason is typed). */
export const DEFAULT_TWO_FACTOR_GATE_COPY: TwoFactorGateCopy = {
    title: 'Secure your account',
    message: 'Two-factor authentication is required before you can access your organization\'s financial features. Set up an authenticator app or SMS verification below to continue.',
};

/**
 * Reasons that present a HARD gate: the dialog cannot be dismissed (no X;
 * Escape and the backdrop do nothing) and the underlying surface stays withheld until the
 * user enrolls. Everything else is a soft gate the user may back out of.
 */
const HARD_GATE_REASONS: ReadonlySet<TwoFactorGateReason> = new Set([
    TwoFactorGateReason.TREASURY_LOGIN,
    TwoFactorGateReason.TREASURY_LOGIN_CARDHOLDER,
]);

/** True when the given reason must present a non-dismissable hard gate. */
export function isHardGateReason(reason: TwoFactorGateReason | undefined): boolean {
    return !!reason && HARD_GATE_REASONS.has(reason);
}

/**
 * Shared, reason-driven two-factor enrollment gate.
 *
 * Wraps the existing standalone {@link TwoFactorAuthComponent} so enrollment is
 * never re-implemented. The hosted component reports enrollment state through
 * its `enrollmentChecked` output; the moment it reports an enrolled
 * authenticator this dialog emits `closed(true)`. Every other way out emits
 * `false` and the caller must not proceed.
 *
 * The gate hardness is derived from the {@link TwoFactorGateReason}: a
 * TREASURY_LOGIN gate is hard (no X, Escape and the backdrop do nothing — the
 * dashboard is withheld until enrolled), while a TREASURY_KYC gate is
 * dismissable so a user can back out of Treasury setup before verification begins.
 *
 * Opened through `TwoFactorGateDialogStateService.ask()` and rendered once by
 * `app-two-factor-gate-dialog-host`, which creates it per opening.
 */
@Component({
    selector: 'app-two-factor-gate-dialog',
    templateUrl: './two-factor-gate-dialog.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        NgTemplateOutlet,
        TwoFactorAuthComponent,
        GoogleAuthSetupStepComponent,
        SmsAuthSetupStepComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TwoFactorGateDialogComponent
{
    @Input() open = false;

    /** Omitted for the launchpad card's generic self-service ask. */
    @Input() set reason(reason: TwoFactorGateReason | undefined) {
        this.copy = (reason ? TWO_FACTOR_GATE_COPY[reason] : undefined) ?? DEFAULT_TWO_FACTOR_GATE_COPY;
        this.dismissible = !isHardGateReason(reason);
    }

    /** `true` once enrolled; `false` for every other way out. */
    @Output() closed = new EventEmitter<boolean>();

    /**
     * Header copy (OnPush — no template getters). Starts reason-specific and swaps to
     * {@link RECOVERY_CODES_GATE_COPY} when a setup step reaches its show-once stage.
     */
    copy: TwoFactorGateCopy = DEFAULT_TWO_FACTOR_GATE_COPY;

    /** Whether the user may dismiss the gate (soft gate) vs. must enroll (hard gate). */
    dismissible = true;

    /**
     * Set once a setup step shows its recovery codes. By then the method is enrolled and the codes
     * generated server-side, so Escape, the backdrop or an X would discard the only view of them —
     * a soft gate included.
     */
    locked = false;

    /**
     * Which setup step is showing, or null for the method-select (cards) view. Drives the
     * single-surface stepper: the gate swaps its body/footer for the inline setup step rather
     * than opening a nested dialog.
     */
    activeSetup: 'google' | 'sms' | null = null;

    /**
     * Handle the hosted component's enrollment report. Close with `true` only
     * once an authenticator is enrolled; a "not enrolled" report is ignored so
     * the user can finish setup.
     */
    onEnrollmentChecked(enrolled: boolean): void {
        if (enrolled) {
            this.closed.emit(true);
        }
    }

    /** A method card was tapped — swap the cards for the inline setup step (no nested dialog). */
    onSetupRequested(method: 'google' | 'sms'): void {
        this.activeSetup = method;
    }

    /** Back out of the setup step to the method-select view. */
    onStepCancelled(): void {
        this.activeSetup = null;
    }

    /** A setup step reached its show-once recovery-codes stage: describe the codes and lock the dialog. */
    onShowingRecoveryCodes(): void {
        this.copy = RECOVERY_CODES_GATE_COPY;
        this.locked = true;
    }

    /** Enrollment finished inside the step — the gate's job is done. */
    onStepCompleted(): void {
        this.closed.emit(true);
    }

    /** "Maybe later" / "Cancel". */
    onDismiss(): void {
        this.closed.emit(false);
    }

    /**
     * `ph-close`: the X, Escape or the backdrop. The kit fires none of these while the dialog is
     * not dismissible, but a hard or locked gate must never close this way, so the guard does not
     * rest on that (as in the setup steps).
     */
    onPhClose(): void {
        if (this.dismissible && !this.locked) {
            this.onDismiss();
        }
    }
}
